<template>
  <div class="vivo">
    <!-- top bar (mockup app__top) -->
    <div class="row between" style="margin-bottom: 14px">
      <div>
        <div class="pagetitle">{{ $t("employee-add.title") }}</div>
        <div class="crumb">
          {{ $t("common.company") }} / {{ $t("nav.employees") }} / {{ $t("employee-add.crumbNew") }}
        </div>
      </div>
      <button class="btn ghost" type="button" @click="goList">{{ $t("employee-add.cancel") }}</button>
    </div>

    <div v-if="loading" class="state">{{ $t("employee-add.loading") }}</div>
    <div v-else-if="error" class="state">{{ $t("employee-add.error") }}</div>

    <template v-else>
      <div class="panel">
        <div class="panel__head">
          <h4>📋 {{ $t("employee-add.panelTitle") }}</h4>
          <span class="tag ok">{{ $t("employee-add.defaultActive") }}</span>
        </div>
        <div class="panel__body">
          <!-- success / error notes -->
          <div v-if="savedName" class="note info" style="margin-bottom: 12px">
            <b>{{ $t("employee-add.savedTitle") }}</b>{{ $t("employee-add.savedMsg", { name: savedName }) }}
          </div>
          <div v-if="submitError" class="note info" style="margin-bottom: 12px">
            <b>{{ $t("employee-add.submitErrorTitle") }}</b>{{ submitError }}
          </div>

          <div class="form">
            <div class="field">
              <label>{{ $t("employee-add.f.fullName") }} <span class="req">*</span></label>
              <input
                class="input"
                :class="{ err: errors.fullName }"
                v-model="form.fullName"
                :placeholder="$t('employee-add.ph.fullName')"
              />
              <div v-if="errors.fullName" class="hint err">{{ fieldError('fullName') }}</div>
            </div>

            <div class="field">
              <label>{{ $t("employee-add.f.nationalId") }} <span class="req">*</span></label>
              <input
                class="input mono"
                :class="{ err: errors.nationalId }"
                v-model="form.nationalId"
                :placeholder="$t('employee-add.ph.nationalId')"
              />
              <div v-if="errors.nationalId" class="hint err">{{ fieldError('nationalId') }}</div>
              <div v-else-if="warnings.nationalId" class="hint">{{ fieldWarning('nationalId') }}</div>
            </div>

            <div class="field">
              <label>{{ $t("employee-add.f.birthDate") }}</label>
              <input class="input" type="date" v-model="form.birthDate" />
            </div>

            <div class="field">
              <label>{{ $t("employee-add.f.gender") }}</label>
              <select class="select" v-model="form.gender">
                <option value="">{{ $t("employee-add.selectPlaceholder") }}</option>
                <option value="male">{{ $t("employee-add.gender.male") }}</option>
                <option value="female">{{ $t("employee-add.gender.female") }}</option>
              </select>
            </div>

            <div class="field">
              <label>{{ $t("employee-add.f.department") }}</label>
              <select class="select" v-model="form.deptId">
                <option value="">{{ $t("employee-add.selectPlaceholder") }}</option>
                <option v-for="d in departments" :key="d.id" :value="d.id">{{ entityName(d) }}</option>
              </select>
            </div>

            <div class="field">
              <label>{{ $t("employee-add.f.policy") }}</label>
              <select class="select" v-model="form.policyId">
                <option value="">{{ $t("employee-add.selectPlaceholder") }}</option>
                <option v-for="p in policies" :key="p.id" :value="p.id">{{ policyLabel(p) }}</option>
              </select>
            </div>

            <div class="field">
              <label>{{ $t("employee-add.f.contractEntity") }} <span v-if="tierRequired" class="req">*</span></label>
              <select class="select" :class="{ err: errors.contractEntityId }" v-model="form.contractEntityId">
                <option value="">{{ $t("employee-add.selectPlaceholder") }}</option>
                <option v-for="t in tiers" :key="t.id" :value="t.id">{{ entityName(t) }}</option>
              </select>
              <div v-if="errors.contractEntityId" class="hint err">{{ fieldError('contractEntityId') }}</div>
              <div v-if="tierHint" class="hint">{{ $t("employee-add." + tierHint) }}</div>
            </div>

            <div class="field">
              <label>{{ $t("employee-add.f.phone") }} <span class="req">*</span></label>
              <input
                class="input mono"
                :class="{ err: errors.phone }"
                v-model="form.phone"
                :placeholder="$t('employee-add.ph.phone')"
              />
              <div v-if="errors.phone" class="hint err">{{ fieldError('phone') }}</div>
              <div v-else-if="warnings.phone" class="hint">{{ fieldWarning('phone') }}</div>
            </div>

            <div class="field">
              <label>{{ $t("employee-add.f.email") }}</label>
              <input
                class="input"
                :class="{ err: errors.email }"
                v-model="form.email"
                :placeholder="$t('employee-add.ph.email')"
              />
              <div v-if="errors.email" class="hint err">{{ fieldError('email') }}</div>
            </div>

            <div class="field">
              <label>{{ $t("employee-add.f.hireDate") }}</label>
              <input class="input" type="date" v-model="form.hireDate" />
            </div>

            <div class="field">
              <label>{{ $t("employee-add.f.cardNumber") }} <span class="req">*</span></label>
              <input
                class="input mono"
                :class="{ err: errors.cardNumber }"
                v-model="form.cardNumber"
                :placeholder="$t('employee-add.ph.cardNumber')"
              />
              <div v-if="errors.cardNumber" class="hint err">{{ fieldError('cardNumber') }}</div>
              <div v-else class="hint">{{ $t("employee-add.cardHint") }}</div>
            </div>

            <div class="field">
              <label>{{ $t("employee-add.f.status") }}</label>
              <select class="select" v-model="form.status">
                <option value="active">{{ $t("employee-add.status.active") }}</option>
                <option value="suspended">{{ $t("employee-add.status.suspended") }}</option>
              </select>
              <span class="tag" :class="statusTag(form.status)" style="align-self: flex-start">
                {{ $t("employee-add.status." + form.status) }}
              </span>
            </div>
          </div>

          <div class="divider"></div>
          <div class="row" style="gap: 10px">
            <button class="btn primary" type="button" :disabled="submitting" @click="submit(true)">
              💾 {{ $t("employee-add.save") }}
            </button>
            <button class="btn soft" type="button" :disabled="submitting" @click="submit(false)">
              💾 {{ $t("employee-add.saveAndAnother") }}
            </button>
            <button class="btn ghost" type="button" @click="goList">{{ $t("employee-add.cancel") }}</button>
          </div>

          <div class="note info mt">
            <b>{{ $t("employee-add.warnTitle") }}</b>{{ $t("employee-add.warnMsg") }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { apiGet, apiPost } from "../../api/vivo/http";
import { companyPath } from "../../api/vivo/session";
import {
  validate,
  buildPayload,
  entityName,
  statusTag,
  isTierRequired,
  shouldClearTierError,
  tierHintKey,
} from "./employee-add-vm";
import { flattenTree } from "./policy-contract-vm";

function blankForm() {
  return {
    fullName: "",
    nationalId: "",
    birthDate: "",
    gender: "",
    deptId: "",
    policyId: "",
    contractEntityId: "",
    phone: "",
    email: "",
    hireDate: "",
    cardNumber: "",
    status: "active",
  };
}

export default {
  name: "VivoEmployeeAdd",
  data() {
    return {
      loading: true,
      error: false,
      submitting: false,
      submitError: "",
      savedName: "",
      departments: [],
      policies: [],
      tiers: [],
      // F1: true only once the contract tree actually loaded — a failed fetch
      // keeps the tier required (fail closed) rather than silently optional.
      tiersLoaded: false,
      // F1-r2: the tree fetch failed — tier stays required, but the hint says why.
      tiersLoadFailed: false,
      form: blankForm(),
      errors: {},
      warnings: {},
    };
  },
  async mounted() {
    try {
      const [depts, pols] = await Promise.all([apiGet("/departments"), apiGet(companyPath("policies"))]);
      this.departments = (depts && depts.data) || [];
      this.policies = (pols && pols.data) || [];
    } catch (e) {
      this.error = true;
    } finally {
      this.loading = false;
    }
    // S10/M12: the tier (الفئة) dropdown = the flattened ACTIVE contract-tree
    // entities (all kinds). Fetched in its OWN try/catch so a missing/forbidden
    // tree never blocks the whole form (the tier select just stays empty).
    try {
      const tree = await apiGet(companyPath("contract-tree"));
      this.tiers = flattenTree((tree && tree.entities) || []).filter((n) => n.isActive !== false);
      this.tiersLoaded = true;
      // F1-r2: a submit made BEFORE the tree loaded flagged the tier 'required';
      // a zero-tier tree makes it optional, so drop that stale error.
      if (shouldClearTierError(this.errors, this.tierOpts)) this.$delete(this.errors, "contractEntityId");
    } catch (e) {
      this.tiers = [];
      this.tiersLoadFailed = true;
    }
  },
  computed: {
    // F1: the tier count the loaded tree offers ({} while unknown → required;
    // { loadFailed } after a failed fetch → still required, F1-r2).
    tierOpts() {
      if (this.tiersLoaded) return { tierCount: this.tiers.length };
      return this.tiersLoadFailed ? { loadFailed: true } : {};
    },
    tierRequired() {
      return isTierRequired(this.tierOpts);
    },
    // F1-r2: i18n key of the hint under the tier select (null = error line only).
    tierHint() {
      return tierHintKey(this.tierOpts, !!this.errors.contractEntityId);
    },
  },
  methods: {
    entityName(entity) {
      return entityName(entity, this.$i18n.locale);
    },
    statusTag(status) {
      return statusTag(status);
    },
    policyLabel(p) {
      if (!p) return "";
      return p.classLabel ? p.number + " — " + p.classLabel : p.number;
    },
    fieldError(field) {
      const code = this.errors[field];
      return code ? this.$t("employee-add.err." + code) : "";
    },
    fieldWarning(field) {
      const code = this.warnings[field];
      return code ? this.$t("employee-add.warn." + code) : "";
    },
    async submit(redirect) {
      this.submitError = "";
      this.savedName = "";
      const result = validate(this.form, this.tierOpts);
      this.errors = result.errors;
      // Warnings (e.g. an NID whose century digit is implausible, or a mobile
      // number that isn't the standard 01-prefixed 11 digits) are shown but
      // never block the save — they're plausibility checks, not hard rules.
      this.warnings = result.warnings;
      if (!result.valid) return;
      this.submitting = true;
      try {
        const created = await apiPost("/employees", buildPayload(this.form));
        const name = (created && created.fullName) || this.form.fullName;
        if (redirect) {
          this.$router.push({ name: "employees" });
        } else {
          this.savedName = name;
          this.form = blankForm();
          this.errors = {};
          this.warnings = {};
        }
      } catch (e) {
        this.submitError = this.$t("employee-add.submitErrorMsg");
      } finally {
        this.submitting = false;
      }
    },
    goList() {
      if (this.$route.name !== "employees") this.$router.push({ name: "employees" });
    },
  },
};
</script>
